import { authStore } from './AuthStore';
import { USER_ROLES } from './RolesAllowed';

/**
 * Central permission keys for frontend authorization.
 * Add new keys here when introducing role-specific UI or routes.
 */
export const PERMISSIONS = Object.freeze({
	ATS_NAV_PIPELINE: 'ats.nav.pipeline',
	ATS_NAV_CANDIDATES: 'ats.nav.candidates',
	ATS_NAV_JOBS: 'ats.nav.jobs',
	ATS_NAV_CLIENTS: 'ats.nav.clients',
	ATS_NAV_EXTERNAL_APPLICATIONS: 'ats.nav.external_applications',
	ATS_NAV_SETTINGS: 'ats.nav.settings',

	APPLICANT_CREATE: 'applicant.create',
	APPLICANT_EDIT: 'applicant.edit',
	APPLICANT_DELETE: 'applicant.delete',
	APPLICANT_BULK_ACTIONS: 'applicant.bulk_actions',
	APPLICANT_ROW_ACTIONS: 'applicant.row_actions',
	APPLICANT_STAGE_CHANGE: 'applicant.stage_change',
	APPLICANT_ASSIGN_JOB: 'applicant.assign_job',
	APPLICANT_TASK_CREATE: 'applicant.task_create',
	APPLICANT_RESUME_MANAGE: 'applicant.resume_manage',

	APPLICANT_TAB_INTERNAL_PROCESS: 'applicant.tab.internal_process',
	APPLICANT_TAB_HISTORY: 'applicant.tab.history',
	APPLICANT_TAB_PROFILE: 'applicant.tab.profile',
	APPLICANT_TAB_CHANGELOG: 'applicant.tab.changelog',

	CANDIDATE_PROFILE_EDIT: 'candidate.profile.edit'
});

const ALL_PERMISSIONS = Object.freeze(Object.values(PERMISSIONS));

const STANDARD_ATS_ROLES = Object.freeze([
	USER_ROLES.ADMIN,
	USER_ROLES.MANAGER,
	USER_ROLES.RECRUITER,
	USER_ROLES.TALENT_LEADER,
	USER_ROLES.ACCOUNT_MANAGER
]);

const ROLE_PERMISSIONS = Object.freeze({
	[USER_ROLES.TECHNICAL_INTERVIEWER]: Object.freeze([
		PERMISSIONS.ATS_NAV_CANDIDATES,
		PERMISSIONS.APPLICANT_TAB_PROFILE,
		PERMISSIONS.CANDIDATE_PROFILE_EDIT
	])
});

const ATS_ROUTE_PERMISSIONS = Object.freeze([
	['/candidates/profile', PERMISSIONS.CANDIDATE_PROFILE_EDIT],
	['/pipeline', PERMISSIONS.ATS_NAV_PIPELINE],
	['/candidates', PERMISSIONS.ATS_NAV_CANDIDATES],
	['/jobs', PERMISSIONS.ATS_NAV_JOBS],
	['/clients', PERMISSIONS.ATS_NAV_CLIENTS],
	['/external-applications', PERMISSIONS.ATS_NAV_EXTERNAL_APPLICATIONS],
	['/settings', PERMISSIONS.ATS_NAV_SETTINGS]
]);

function getPermissionsForRole(role) {
	if (!role) {
		return [];
	}
	if (STANDARD_ATS_ROLES.includes(role)) {
		return ALL_PERMISSIONS;
	}
	return ROLE_PERMISSIONS[role] || [];
}

export function hasPermission(permission) {
	const role = authStore.profile?.role;
	return getPermissionsForRole(role).includes(permission);
}

export function hasAnyPermission(permissions) {
	return permissions.some(permission => hasPermission(permission));
}

export function canAccessAtsRoute(routePath) {
	const normalizedPath = routePath.replace(/\/$/, '');
	const permission = ATS_ROUTE_PERMISSIONS.find(([route]) =>
		normalizedPath.startsWith(route)
	)?.[1];

	if (!permission) {
		return true;
	}

	return hasPermission(permission);
}

export function getDefaultAtsPath() {
	if (hasPermission(PERMISSIONS.ATS_NAV_PIPELINE)) {
		return '/ats/pipeline';
	}
	if (hasPermission(PERMISSIONS.ATS_NAV_CANDIDATES)) {
		return '/ats/candidates';
	}
	return '/ats/candidates';
}

export function getAllowedApplicantProfileTabs() {
	return [
		{
			id: 'internal_process',
			permission: PERMISSIONS.APPLICANT_TAB_INTERNAL_PROCESS
		},
		{ id: 'history', permission: PERMISSIONS.APPLICANT_TAB_HISTORY },
		{ id: 'profile', permission: PERMISSIONS.APPLICANT_TAB_PROFILE },
		{ id: 'changelog', permission: PERMISSIONS.APPLICANT_TAB_CHANGELOG }
	]
		.filter(tab => hasPermission(tab.permission))
		.map(tab => tab.id);
}

export function getDefaultApplicantProfileTab() {
	const allowedTabs = getAllowedApplicantProfileTabs();
	return allowedTabs.includes('profile') && allowedTabs.length === 1
		? 'profile'
		: allowedTabs[0] || 'profile';
}

const APPLICANT_PROFILE_TAB_INDEX = Object.freeze({
	internal_process: 1,
	history: 0,
	profile: 2,
	changelog: 3
});

export function getApplicantProfileTabIndex(
	tabId = getDefaultApplicantProfileTab()
) {
	return (
		APPLICANT_PROFILE_TAB_INDEX[tabId] ?? APPLICANT_PROFILE_TAB_INDEX.profile
	);
}

export function isApplicantProfileTabAllowed(tabId) {
	return getAllowedApplicantProfileTabs().includes(tabId);
}
